// Shared answer-matching utilities used by StudentPaperTake (practice-mode verify)
// and StudentPaperReview (post-paper review correctness display).

// A literal &nbsp; entity (from stored HTML answers/questions) is a space, not text — decode it (plus
// the numeric forms) so tokens like "&nbsp;6.531" compare as "6.531".
export const decodeHtmlSpaces = (v) => String(v ?? "").replace(/&nbsp;|&#160;|&#xa0;/gi, " ");

// Some authored answers (e.g. inequality signs "<"/">") were stored as literal HTML entities
// ("&lt;"/"&gt;") rather than the raw character. Decode any remaining entities after tag-stripping
// so they don't show up as literal "&lt;" text in plain-text answer boxes (which, unlike
// HtmlWithLatex, don't run content through the browser's HTML parser).
export const decodeHtmlEntities = (v) => {
  const s = String(v ?? "");
  if (!/&(?:#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]+);/.test(s)) return s;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = s;
  return textarea.value;
};

// Extracted mark schemes carry a stray space between a minus sign and its digits ("-&nbsp;7°C"), so
// the value reads as +7 and a correct "-7" is scored wrong. Re-glue the sign, but only where a value
// STARTS (string start or after a list separator) — never after a digit, or the expression "5 - 3"
// would turn into "5 -3".
export const glueLeadingSign = (v) =>
  String(v ?? "").replace(/(^|[,;|(]\s*)[-−–—]\s+(?=[.\d])/g, "$1-");

export const normalizeVal = (v) => glueLeadingSign(decodeHtmlSpaces(v).replace(/\s+/g, " ").trim()).toLowerCase().replace(/÷/g, '/');

export const normalizeHyphensAndBrackets = (v) =>
  String(v ?? "").replace(/(\w)-(\w)/g, '$1 $2').replace(/[()[\]{}]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

export const stripAnswerBrackets = (val) => {
  if (!val) return val;
  try {
    const p = JSON.parse(val);
    if (Array.isArray(p)) return p.flat(Infinity).map((v) => String(v ?? '').trim()).filter(Boolean).join(' / ');
  } catch { /* not JSON */ }
  return val;
};

export const stripMarkSchemeOperatorPrefix = (v) =>
  String(v ?? "")
    .trim()
    .replace(/^\(\s*(?:\\[a-z]+|[-+x×*÷/])\s*\)\s*/iu, "")
    .trim();

// Strip trailing prime/apostrophe variants so A′ matches A (coordinate geometry notation).
export const stripPrime = (v) => String(v ?? "").replace(/[′'ʹ']+/g, "").trim();

// am/pm may be written with dots ("a.m", "p.m") — e.g. an authored answer-unit suffix of "a.m" gets
// appended to the typed value as "11.20 a.m". Match both the plain and dotted forms throughout.
const AMPM_RE = "a\\.?\\s*m\\.?|p\\.?\\s*m\\.?";

export const parseClockTime = (value) => {
  let v = String(value ?? "").toLowerCase().trim().replace(/\s+/g, " ");
  if (!v) return null;

  const colon = v.match(/^(\d{1,2})[:.](\d{2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)?$/i);
  if (colon) {
    const hour = parseInt(colon[1], 10);
    const minute = parseInt(colon[2], 10);
    if (minute >= 60 || hour > 23) return null;
    const periodRaw = colon[3];
    let period = null;
    if (periodRaw) {
      period = periodRaw.replace(/\s/g, "").startsWith("a") ? "am" : "pm";
    }
    return { hour, minute, period };
  }

  const dotted = v.match(/^(\d{1,2})\.(\d{2})$/);
  if (dotted) {
    const hour = parseInt(dotted[1], 10);
    const minute = parseInt(dotted[2], 10);
    if (minute < 60 && hour <= 23) {
      return { hour, minute, period: null };
    }
  }

  return null;
};

const extractWrittenAmPm = (value) => {
  const v = String(value ?? "").toLowerCase();
  if (/(?:^|(?<=\d))\s*a\.?\s*m\.?(?:\b|$)/i.test(v)) return "am";
  if (/(?:^|(?<=\d))\s*p\.?\s*m\.?(?:\b|$)/i.test(v)) return "pm";
  return null;
};

const clockTimePeriodsCompatible = (studentRaw, correctRaw, studentPeriod, correctPeriod) => {
  const studentWritten = extractWrittenAmPm(studentRaw);
  const correctWritten = extractWrittenAmPm(correctRaw);
  const expected = correctWritten ?? correctPeriod;
  if (studentWritten === null) return true;
  if (expected === null) return true;
  return studentWritten === expected;
};

export const clockTimeAnswersEquivalent = (student, correct) => {
  const s = parseClockTime(student);
  const c = parseClockTime(correct);
  if (!s || !c) return false;
  if (s.hour !== c.hour || s.minute !== c.minute) return false;
  return clockTimePeriodsCompatible(student, correct, s.period, c.period);
};

export const normalizeTimeForMatch = (v) => {
  const parsed = parseClockTime(v);
  if (parsed) {
    return `${parsed.hour}:${String(parsed.minute).padStart(2, "0")}`;
  }
  let s = String(v ?? "").replace(/^(\d{1,2})\.(\d{2})\s*(am|pm)?$/i, (_, h, m) => `${h}:${m}`);
  s = s.replace(/\s*(am|pm)$/i, "").trim();
  return s;
};

// A clock-time mark scheme is decided solely by timesMatch (mirrors the backend's
// correctAnswerIsClockTime guard): the fuzzy tiers below collapse whitespace and split "12:55" into a
// {12,55} token set, which would re-accept the wider gaps the one-space limit above rejects. The mark
// scheme is authored HTML so its own whitespace is noise — only the submission is held to one space.
export const correctIsClockTime = (v) =>
  new RegExp(`^\\d{1,2}\\s*[.:]\\s*\\d{2}\\s*(${AMPM_RE})?$`, "i").test(
    decodeHtmlSpaces(v).replace(/\s+/g, " ").trim()
  );

// UK time notation uses a dot ("17.50") as well as a colon ("17:50"), so a dot-separated submission
// with a two-digit minute part is the same time as the colon mark scheme — accept it. A decimal like
// "17.5" has only one fractional digit, so parseClockTime (which requires \d{2} minutes) never
// converts it to a time and it stays incorrect.
// Asymmetric: a bare dotted mark scheme ("12.15" scale reading) must NOT accept a colon ("12:15").
// With am/pm the field is unambiguously a clock time, so either separator is fine.
export const timesMatch = (submitted, correct) => {
  const sRaw = String(submitted);
  const cRaw = String(correct).replace(/\s+/g, " ");
  const hasAmPm = (v) => new RegExp(`(?:${AMPM_RE})`, "i").test(v);
  if (!hasAmPm(sRaw) && !hasAmPm(cRaw)) {
    const sep = (v) => (/\d\s*:\s*\d/.test(v) ? ":" : /\d\s*\.\s*\d/.test(v) ? "." : null);
    if (sep(cRaw) === "." && sep(sRaw) === ":") return false;
  }
  return normalizeTimeForMatch(sRaw) === normalizeTimeForMatch(cRaw)
    || clockTimeAnswersEquivalent(submitted, correct);
};

export const normalizeVulgarFractions = (v) => {
  const map = {
    '½': ' 1/2', '⅓': ' 1/3', '⅔': ' 2/3',
    '¼': ' 1/4', '¾': ' 3/4',
    '⅕': ' 1/5', '⅖': ' 2/5', '⅗': ' 3/5', '⅘': ' 4/5',
    '⅙': ' 1/6', '⅚': ' 5/6',
    '⅐': ' 1/7', '⅛': ' 1/8', '⅜': ' 3/8', '⅝': ' 5/8', '⅞': ' 7/8',
    '⅑': ' 1/9', '⅒': ' 1/10',
  };
  return String(v ?? '').replace(/\u2044/g, '/').replace(/[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅐⅛⅜⅝⅞⅑⅒]/g, (ch) => map[ch] ?? ch).trim();
};

export const normalizeLatexFraction = (v) => {
  let s = normalizeVulgarFractions(v);
  s = s.replace(/^\$+|\$+$/g, "").trim();
  // Admins author maths as \(\frac{21}{4}\); drop the delimiters so a LaTeX-formatted mark
  // scheme still compares against a student's plain "21/4".
  s = s.replace(/^\\[([]\s*|\s*\\[)\]]$/g, "").trim();
  s = s.replace(/(\d)\\frac\{([^}]+)\}\{([^}]+)\}/g, "$1 $2/$3");
  s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "$1/$2");
  return s;
};

// The degree branch has no \b (° is not a word character), so "-7°C" / "-7 °c" / "45°" strip to the
// bare number — the backend's normalizeFuzzyAnswer already did this, and the drift made the client
// override a correct server verdict on temperature questions.
export const stripCommonUnit = (v) =>
  v.replace(/\s*(?:°\s*[cf]?|\b(?:kg|g|mg|cm|mm|km|m|ml|l|litres?|liters?|metres?|meters?|degrees?\s*(?:celsius|fahrenheit)?|celsius|fahrenheit))\s*(?:\^?\s*[²³23])?\s*$/i, "").trim();

// A plain number with no unit — "18", "18.5", "-3", "3/4", "1 1/2", "1,200", "1 000". Used to enforce:
// when the mark scheme is JUST a number, the student answer must be too (no appended unit/word). The
// value carries no "cm"/"m"/"kg" etc. — anything with a trailing unit is NOT a plain number.
export const isPlainNumberVal = (v) => {
  // A LaTeX-authored mark scheme ("5 \frac{23}{24}") must be recognised as a plain mixed number
  // too, or this check silently no-ops for it and the "submission must also be a plain number"
  // guard elsewhere never fires.
  const s = normalizeLatexFraction(String(v ?? "")).trim().toLowerCase();
  if (s === "") return false;
  return /^[-+]?\d{1,3}(?:[,\s]\d{3})+(?:\.\d+)?$/.test(s)   // grouped thousands "1,200" / "1 000"
    || /^[-+]?\d+(?:\.\d+)?$/.test(s)                         // integer / decimal
    || /^[-+]?\d+\s*\/\s*\d+$/.test(s)                        // fraction "3/4"
    || /^[-+]?\d+\s+\d+\s*\/\s*\d+$/.test(s);                 // mixed number "1 1/2"
};

const answerTokensForMatch = (v) =>
  normalizeVal(stripMarkSchemeOperatorPrefix(v)).split(" ").filter(Boolean);

export const questionAsksForCount = (questionText) => {
  const q = decodeHtmlSpaces(String(questionText ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return /\b(?:how many|number of)\b/.test(q);
};

const countSuffixAppearsInQuestion = (suffix, questionText) => {
  const stem = String(suffix ?? "").toLowerCase().replace(/[^a-z]/g, "");
  if (stem.length < 2) return false;
  const q = decodeHtmlSpaces(String(questionText ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const base = stem.replace(/s$/, "");
  return new RegExp(`\\b${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}s?\\b`, "i").test(q);
};

export const isOrderedTokenPrefix = (submitted, correct) => {
  const short = answerTokensForMatch(submitted);
  const long = answerTokensForMatch(correct);
  if (short.length === 0 || long.length === 0 || short.length >= long.length) return false;
  return short.every((token, index) => token === long[index]);
};

/** "How many edges?" — bare "12" is fully correct vs mark scheme "12 edges". */
export const countQuestionNumericAnswerEquivalent = (submitted, correct, questionText) => {
  if (!questionAsksForCount(questionText) || !isOrderedTokenPrefix(submitted, correct)) return false;
  const short = answerTokensForMatch(submitted);
  const long = answerTokensForMatch(correct);
  if (long.length !== short.length + 1) return false;
  const suffix = long[long.length - 1];
  if (!countSuffixAppearsInQuestion(suffix, questionText)) return false;
  if (!short.every((token) => isPlainNumberVal(token))) return false;
  return short.every((token, index) => token === long[index]);
};

export const parseValueWithUnit = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  const poundMatch = s.match(/^£\s*(\d+(?:\.\d+)?)\s*$/);
  if (poundMatch) return [parseFloat(poundMatch[1]) * 100, "p", true];
  const unitMatch = s.match(/^(-?\d+(?:\.\d+)?)\s*(km²|cm²|m²|km\/h|m\/s|seconds?|minutes?|hours?|days?|secs?|mins?|hrs?|mm|cm|km|mg|kg|ml|m|g|l|p)\s*$/);
  if (unitMatch) return [parseFloat(unitMatch[1]), unitMatch[2]];
  if (/^-?\d+(?:\.\d+)?\s*$/.test(s)) return [parseFloat(s), ""];
  // Stray trailing "o"/"O" typo on a number whose unit is supplied by the field's unit
  // suffix (e.g. "1o" typed in a metres field) — read it as the leading number.
  const strayO = s.match(/^(-?\d+(?:\.\d+)?)o+$/);
  if (strayO) return [parseFloat(strayO[1]), ""];
  return null;
};

export const UNIT_CONVERSIONS = {
  mm: [1, "mm", "length"], cm: [10, "mm", "length"], m: [1000, "mm", "length"], km: [1000000, "mm", "length"],
  "cm²": [100, "mm²", "area"], "m²": [1000000, "mm²", "area"], "km²": [1e12, "mm²", "area"],
  mg: [1, "mg", "weight"], g: [1000, "mg", "weight"], kg: [1000000, "mg", "weight"],
  ml: [1, "ml", "volume"], l: [1000, "ml", "volume"],
  p: [1, "p", "currency"],
  s: [1, "s", "time"], sec: [1, "s", "time"], secs: [1, "s", "time"],
  second: [1, "s", "time"], seconds: [1, "s", "time"],
  min: [60, "s", "time"], mins: [60, "s", "time"], minute: [60, "s", "time"], minutes: [60, "s", "time"],
  hr: [3600, "s", "time"], hrs: [3600, "s", "time"], hour: [3600, "s", "time"], hours: [3600, "s", "time"],
  day: [86400, "s", "time"], days: [86400, "s", "time"],
};

export const PARENT_CHILD_FACTOR = { p: 100, mm: 10, cm: 100, m: 1000, mg: 1000, g: 1000, ml: 1000 };

/**
 * Parse compound values like "1 hour 30 min", "1 m 50 cm", "2 kg 300 g".
 * Returns [canonicalValue, canonUnit, dimension] or null.
 */
export const parseCompoundValue = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return null;
  const pattern = /(-?\d+(?:\.\d+)?)\s*(km²|cm²|m²|seconds?|minutes?|hours?|days?|secs?|mins?|hrs?|mm|cm|km|mg|kg|ml|m|g|l|p)/g;
  const matches = [...s.matchAll(pattern)];
  if (matches.length < 2) return null;
  let total = 0;
  let canonUnit = null;
  let canonDim = null;
  for (const m of matches) {
    const num = parseFloat(m[1]);
    const unit = m[2];
    const conv = UNIT_CONVERSIONS[unit];
    if (!conv) return null;
    const [factor, cu, cd] = conv;
    if (canonDim === null) { canonDim = cd; canonUnit = cu; }
    else if (cd !== canonDim) return null;
    total += num * factor;
  }
  return canonDim !== null ? [total, canonUnit, canonDim] : null;
};

export const isUnitEquivalent = (submitted, correct) => {
  const sp = parseValueWithUnit(submitted);
  const cp = parseValueWithUnit(correct);

  if (sp && cp) {
    const [sNum, sUnit] = sp;
    const [cNum, cUnit] = cp;
    if (sUnit && cUnit) {
      const sc = UNIT_CONVERSIONS[sUnit];
      const cc = UNIT_CONVERSIONS[cUnit];
      if (sc && cc && sc[2] === cc[2]) return Math.abs(sNum * sc[0] - cNum * cc[0]) < 0.001;
      return false;
    }
    if (!sUnit && cUnit) {
      const factor = PARENT_CHILD_FACTOR[cUnit];
      if (factor && Math.abs(sNum * factor - cNum) < 0.001) return true;
      // A £ value (e.g. "£0.71") is stored as its pence magnitude (71). In a £ field a bare
      // number means pounds, so "71" is £71 — don't accept it via the bare-pence fallback.
      if (cp[2]) return false;
      return Math.abs(sNum - cNum) < 0.001;
    }
    if (sUnit && !cUnit) {
      const factor = PARENT_CHILD_FACTOR[sUnit];
      if (factor && Math.abs(cNum * factor - sNum) < 0.001) return true;
      if (sp[2]) return false;
      return Math.abs(sNum - cNum) < 0.001;
    }
  }

  // Compound value check: "1 hour 30 min" vs "90 min", "1 m 50 cm" vs "150 cm", etc.
  const sCompound = parseCompoundValue(submitted);
  const cCompound = parseCompoundValue(correct);
  if (sCompound !== null || cCompound !== null) {
    const resolveCanon = (compound, parsed) => {
      if (compound) return compound;
      if (parsed && parsed[1]) {
        const conv = UNIT_CONVERSIONS[parsed[1]];
        if (conv) return [parsed[0] * conv[0], conv[1], conv[2]];
      }
      return null;
    };
    const sCanon = resolveCanon(sCompound, sp);
    const cCanon = resolveCanon(cCompound, cp);
    if (sCanon && cCanon && sCanon[2] === cCanon[2]) {
      return Math.abs(sCanon[0] - cCanon[0]) < 0.001;
    }
  }

  return false;
};

/** Fractional digit count on a bare number ("4.71" → 2, "4.7" → 1, "5" → 0). Null if not numeric. */
export const decimalPlaceCount = (value) => {
  const bare = stripCommonUnit(String(value ?? "").trim().toLowerCase().replace(/[£$€¥%]/g, "")).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(bare)) return null;
  const dot = bare.indexOf(".");
  return dot === -1 ? 0 : bare.length - dot - 1;
};

/**
 * Mark scheme to N d.p. ("4.7 mm") — a more precise working value that rounds to it ("4.71") is accepted.
 * Integer schemes stay strict ("5.1" ≠ "5").
 */
export const roundedDecimalPrecisionMatch = (submitted, correct) => {
  const cDp = decimalPlaceCount(correct);
  const sDp = decimalPlaceCount(submitted);
  if (cDp === null || sDp === null || cDp < 1 || sDp <= cDp) return false;
  const sn = plainNumericValue(stripCommonUnit(String(submitted ?? "").trim()));
  const cn = plainNumericValue(stripCommonUnit(String(correct ?? "").trim()));
  if (sn === null || cn === null) return false;
  const factor = 10 ** cDp;
  return Math.abs(Math.round(sn * factor) / factor - cn) < 1e-9;
};

// Split on newlines, semicolons, space-pipe-space (" | ", the frontend's multi-fill
// joiner), and commas — but NOT thousands-separator commas (digit,digit{3}non-digit).
// "B and D" is normalised to "B, D" before splitting.
const expandMultiValueAndJoiners = (v) => {
  let out = String(v ?? "").trim();
  if (!out) return out;
  let prev = "";
  while (prev !== out) {
    prev = out;
    out = out.replace(/\b([A-Za-z])\s+and\s+([A-Za-z])\b/gi, "$1, $2");
    out = out.replace(/\b(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)\b/g, "$1, $2");
  }
  return out;
};

export const splitMultiValueAnswer = (v) =>
  expandMultiValueAndJoiners(decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " "))
    .split(/[\n\r;]|\s+\|\s+|,(?!\d{3}(?!\d))/)
    .map((p) => p.trim())
    .filter(Boolean);

// A single number mistyped with a comma decimal separator ("3,00", "£3,5", "3,0 m") — one comma
// between digits with 1–2 fractional digits, ignoring a leading currency symbol and a trailing unit.
// Not a list ("3, 5, 7") or thousands group ("1,200").
export const isSingleMalformedNumber = (v) => {
  let s = decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").trim().replace(/^[£$€¥]/, "").trim();
  s = s.replace(/\s*(?:mm|cm|km|kg|mg|ml|m|g|l|p|cm²|cm2|m²|m2|km²|km2|°c|°f|°)\s*$/iu, "").trim();
  return /^-?\d+,\d{1,2}$/.test(s);
};

export const extractCorrectAlternatives = (cf) => {
  if (cf.includes(" (or) ")) return cf.split(" (or) ").map((p) => p.trim()).filter(Boolean);
  if (/ or /i.test(cf)) return cf.split(/ or /i).map((p) => p.trim()).filter(Boolean);
  if (cf.includes(" / ")) return cf.split(" / ").map((p) => p.trim()).filter(Boolean);
  return [cf];
};

// A bare ordinal equals its digit ("3rd" ≈ "3"). Only collapse a standalone ordinal — NOT an ordinal
// that begins a longer phrase, or "1st November 1998" (a date) would wrongly reduce to "1" and match
// any answer starting with "1st".
export const normalizeOrdinal = (v) =>
  v.replace(/^(\d+)(?:st|nd|rd|th)$/, "$1");

export const extractAnswerCandidates = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return [];
  const candidates = [raw];
  if (raw.includes("=")) {
    const parts = raw.split("=");
    const rhs = String(parts[parts.length - 1] ?? "").trim();
    if (rhs) candidates.push(rhs);
  }
  return Array.from(new Set(candidates.filter(Boolean)));
};

// Numeric value of a plain number, accepting a real thousands separator ("1,200" / "1 200" → 1200) but
// NOT a comma-decimal typo ("8,7", "12,24") — a comma is only a separator when exactly 3 digits follow
// it. Mirrors FuzzyAnswerMatching::parseSimpleNumericValue so the client agrees with the server.
export const plainNumericValue = (v) => {
  const s = decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, "").trim().toLowerCase();
  if (s === "") return null;
  if (/,(?!\d{3}(?!\d))/.test(s)) return null;
  const bare = s.replace(/[,\s]/g, "");
  if (!/^[-+]?\d+(?:\.\d+)?$/.test(bare)) return null;
  const n = parseFloat(bare);
  return Number.isFinite(n) ? n : null;
};

// The comparison operator an answer states, canonicalised — "=/" and "!=" are both ≠, "<=" is ≤. Null
// when the text is not (just) an operator. "Complete the expression with <, > or =" answers are decided
// EXACTLY: the fuzzy tiers drop the "/" from "=/" (reading a ≠ as "="), and tag-stripping turns a bare
// "<" into an empty string that matches anything else normalising to empty.
// Mirrors FuzzyAnswerMatching::canonicalComparisonOperator.
export const canonicalComparisonOperator = (value) => {
  const v = decodeHtmlSpaces(String(value ?? ""))
    .replace(/^<p>|<\/p>$|<br\s*\/?>/gi, "")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&ne;/gi, "≠")
    .replace(/&le;/gi, "≤")
    .replace(/&ge;/gi, "≥")
    // LaTeX spellings from the math-tools keyboard, resolved before the literal map below so the
    // remaining backslash stays meaningful: "=\" is a struck-through equals (a ≠), not an "=" plus noise.
    .replace(/\\not\s*=/gi, "≠")
    .replace(/\\neq?\b/gi, "≠")
    .replace(/\\leq?\b/gi, "≤")
    .replace(/\\geq?\b/gi, "≥")
    .replace(/\\lt\b/gi, "<")
    .replace(/\\gt\b/gi, ">")
    .replace(/[\s$]+/g, "");
  if (!v) return null;
  const map = {
    "=": "=", "<": "<", ">": ">",
    "≤": "≤", "<=": "≤", "=<": "≤",
    "≥": "≥", ">=": "≥", "=>": "≥",
    // A ≠ is drawn by striking the equals through, and students type that stroke either way.
    "≠": "≠", "!=": "≠", "=!": "≠", "=/": "≠", "/=": "≠", "=/=": "≠", "=\\": "≠", "\\=": "≠", "=\\=": "≠",
  };
  return map[v] ?? null;
};

// A mark scheme naming TWO things joined by "and" — "Birmingham and London", "3 and 5". Both are
// required ("Which TWO cities…"), so half of it — or the unfinished "Birmingham and" — is wrong. Each
// side must be a proper noun or a number, so an explanatory sentence containing "and" isn't a pair.
// Mirrors FuzzyAnswerMatching::correctIsNamedPair.
export const correctIsNamedPair = (correct) => {
  const t = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!t || t.includes(",") || /\bor\b/i.test(t)) return null;
  const m = t.match(/^(.+?)\s+and\s+(.+)$/i);
  if (!m) return null;
  const side = /^(?:\p{Lu}[\p{L}'’-]*(?:\s+\p{Lu}[\p{L}'’-]*)?|-?\d+(?:\.\d+)?\s*[\p{L}%°]{0,4})$/u;
  const a = m[1].trim();
  const b = m[2].trim();
  return side.test(a) && side.test(b) ? [a, b] : null;
};

// A sequence of single-letter labels — a route through stations ("A E D B C G"), a reordering
// ("B C A"), a word reflected letter by letter ("S C I T A M E H T A M"). Every letter is part of the
// answer and their ORDER is the answer, so a prefix of it ("A E") is wrong, not "most of the way
// there". Three or more letters: a two-letter answer is too easily an abbreviation.
// Mirrors FuzzyAnswerMatching::correctIsLabelSequence.
export const correctIsLabelSequence = (correct) => {
  const t = decodeHtmlSpaces(String(correct ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!/^(?:\p{L}\s+){2,}\p{L}$/u.test(t)) return null;
  return t.replace(/\s+/g, "").toUpperCase();
};

// Separators are free ("AEDBCG", "A, E, D, B, C, G", "A → E → D → B → C → G"); letters and order aren't.
export const labelSequenceMatches = (submitted, correct) => {
  const expected = correctIsLabelSequence(correct);
  if (!expected) return false;
  const s = decodeHtmlSpaces(String(submitted ?? ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/(?:->|→|[\s,;.\-–—>])+/gu, "")
    .toUpperCase();
  return s !== "" && s === expected;
};

// Both names present, in any order — "London and Birmingham" answers "Birmingham and London".
export const namedPairMatches = (submitted, pair) => {
  const s = decodeHtmlSpaces(String(submitted ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  if (!s) return false;
  return pair.every((item) => {
    const needle = item.trim().toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(?<![\\p{L}\\d])${needle}(?![\\p{L}\\d])`, "u").test(s);
  });
};

// A sum of two or more measured values: "50p+10p", "20p + 20p + 20p", "3 + 4". The suffix must be a
// UNIT, not an algebra variable. Mirrors FuzzyAnswerMatching::isSumExpression.
const SUM_TERM = String.raw`\d+(?:\.\d+)?\s*(?:p|pence|pounds?|kg|mg|g|km|cm|mm|m|ml|l|%|°c?|²|³)?`;
export const isSumExpression = (value) => {
  const t = decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    .replace(/[\s,.;]+$/, "");
  if (!t || !t.includes("+")) return false;
  return new RegExp(`^${SUM_TERM}(?:\\s*\\+\\s*${SUM_TERM})+$`, "i").test(t);
};

// Terms of a sum, sorted — "20p + 10p + 20p" and "20p + 20p + 10p" are the same answer.
export const sumExpressionCanonical = (value) =>
  decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, " ").toLowerCase()
    .replace(/[\s,.;]+$/, "")
    .split("+")
    .map((p) => p.replace(/\s+/g, ""))
    .filter(Boolean)
    .sort()
    .join("+");

// A mark scheme that lists two or more sums ("List all the ways Penny can make 60p") — every one of
// them is part of the answer, so a subset of the list is partial, never correct.
export const correctIsSumEnumeration = (correct) => {
  const parts = splitMultiValueAnswer(String(correct ?? ""));
  return parts.length > 1 && parts.every(isSumExpression);
};

export const sumEnumerationMatches = (submitted, correct) => {
  const canonSet = (v) => Array.from(new Set(
    splitMultiValueAnswer(String(v ?? "")).map(sumExpressionCanonical).filter(Boolean)
  ));
  const expected = canonSet(correct);
  const given = canonSet(submitted);
  return given.length === expected.length && expected.every((e) => given.includes(e));
};

// Fold <sup>n</sup> / unicode superscripts / 10^{2} to caret form before tag-stripping would glue
// "1<sup>3</sup>" into "13". Mirrors AlgebraicExpressionMatcher::normalize (PHP).
export const normalizeNumericPowerExpr = (v) => {
  let t = String(v ?? "");
  t = t.replace(/<sup[^>]*>\s*([^<]*?)\s*<\/sup>/gi, "^$1");
  t = decodeHtmlEntities(decodeHtmlSpaces(t).replace(/<[^>]*>/g, " "));
  const supMap = {
    "⁰": "^0", "¹": "^1", "²": "^2", "³": "^3", "⁴": "^4",
    "⁵": "^5", "⁶": "^6", "⁷": "^7", "⁸": "^8", "⁹": "^9",
  };
  t = t.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (ch) => supMap[ch] ?? ch);
  t = t.replace(/\^(\d)\^(\d)/g, "^$1$2");
  t = t.replace(/\^\s*\{\s*([^{}]*)\s*\}/g, "^$1");
  t = t.replace(/[×⋅·]/g, "*").replace(/÷/g, "/").replace(/[−–—]/g, "-");
  t = t.replace(/\s+/g, " ").trim().toLowerCase().replace(/[=.\s]+$/g, "");
  return t;
};

export const isNumericPowerExpression = (correct) => {
  const t = normalizeNumericPowerExpr(correct);
  return t !== ""
    && t.includes("^")
    && /^[0-9+\-*/^(). ]+$/.test(t)
    && /\d/.test(t);
};

const powerCountOf = (normalized) => (normalized.match(/\^/g) || []).length;

const exponentMultisetOf = (normalized) => {
  const exps = [...normalized.matchAll(/\^(\d+(?:\.\d+)?)/g)].map((m) => m[1]);
  exps.sort();
  return exps.join(",");
};

// Lightweight evaluator for digit/operator/bracket/^ expressions (no variables). Returns null if unparseable.
const evalNumericPowerExpr = (expr) => {
  const s = String(expr ?? "").replace(/\s+/g, "");
  if (!s || !/^[0-9+\-*/^().]+$/.test(s)) return null;
  let pos = 0;
  const peek = () => (pos < s.length ? s[pos] : "");
  const parseExpr = () => {
    let value = parseTerm();
    if (value === null) return null;
    while (peek() === "+" || peek() === "-") {
      const op = peek();
      pos++;
      const rhs = parseTerm();
      if (rhs === null) return null;
      value = op === "+" ? value + rhs : value - rhs;
    }
    return value;
  };
  const parseTerm = () => {
    let value = parseFactor();
    if (value === null) return null;
    while (true) {
      const c = peek();
      if (c === "*" || c === "/") {
        pos++;
        const rhs = parseFactor();
        if (rhs === null) return null;
        if (c === "/") {
          if (Math.abs(rhs) < 1e-12) return null;
          value /= rhs;
        } else {
          value *= rhs;
        }
        continue;
      }
      if (c === "(" || /\d/.test(c)) {
        const rhs = parseFactor();
        if (rhs === null) return null;
        value *= rhs;
        continue;
      }
      return value;
    }
  };
  const parseFactor = () => {
    const c = peek();
    if (c === "+" || c === "-") {
      pos++;
      const v = parseFactor();
      return v === null ? null : (c === "-" ? -v : v);
    }
    let base = null;
    if (c === "(") {
      pos++;
      base = parseExpr();
      if (base === null || peek() !== ")") return null;
      pos++;
    } else if (/\d|\./.test(c)) {
      const start = pos;
      while (pos < s.length && /[\d.]/.test(s[pos])) pos++;
      const num = Number(s.slice(start, pos));
      if (!Number.isFinite(num)) return null;
      base = num;
    } else {
      return null;
    }
    if (peek() === "^") {
      pos++;
      const exp = parseFactor();
      if (exp === null) return null;
      base **= exp;
    }
    return base;
  };
  const value = parseExpr();
  return (value !== null && pos === s.length && Number.isFinite(value)) ? value : null;
};

// Mirrors AlgebraicExpressionMatcher::matchesNumericPower — form matters, not just evaluated value.
export const matchesNumericPower = (submitted, correct) => {
  const a = normalizeNumericPowerExpr(submitted);
  const b = normalizeNumericPowerExpr(correct);
  if (!a || !a.includes("^")) return false;
  if (a.replace(/\s+/g, "") === b.replace(/\s+/g, "")) return true;
  if (powerCountOf(a) !== powerCountOf(b)) return false;
  if (exponentMultisetOf(a) !== exponentMultisetOf(b)) return false;
  const va = evalNumericPowerExpr(a);
  const vb = evalNumericPowerExpr(b);
  if (va === null || vb === null) return false;
  return Math.abs(va - vb) <= 1e-9 * Math.max(1, Math.abs(vb));
};

export const singleValueMatches = (submitted, correct) => {
  // The operator IS the answer ("complete with <, > or =") — compare it exactly, before the tiers below
  // normalise "=/" into "=" or reduce a bare "<" to nothing.
  const expectedOperator = canonicalComparisonOperator(correct);
  if (expectedOperator) return canonicalComparisonOperator(submitted) === expectedOperator;

  // Power mark schemes are decided on form before sum / unit / digit-reducing tiers.
  if (isNumericPowerExpression(correct)) {
    return matchesNumericPower(submitted, correct);
  }

  const s = normalizeVal(stripMarkSchemeOperatorPrefix(submitted));
  const c = normalizeVal(stripMarkSchemeOperatorPrefix(correct));
  if (!s || !c) return false;

  // "counter-clockwise" is the OPPOSITE of "clockwise", and "(30 × 10) \ 50" is not "÷ 50" — both are
  // wrong before any tier below gets to normalise them into a match.
  if (rotationDirectionConflict(submitted, correct) || hasStrayBackslash(submitted)) return false;
  if (rotationDirectionsAgree(submitted, correct)) return true;

  // Two sums differ by how many times a term appears ("20p + 20p" is not "20p + 20p + 20p"), which the
  // unit-stripping and no-space tiers below throw away. Compare the terms as a multiset instead.
  if (isSumExpression(submitted) && isSumExpression(correct)) {
    return sumExpressionCanonical(submitted) === sumExpressionCanonical(correct);
  }

  if (correctIsClockTime(c)) return timesMatch(submitted, correct);

  if (s === c) return true;

  // The mark scheme is JUST a number (a count/value, no unit) — the answer must be a plain number too.
  // Reject anything with an appended unit or word ("18m", "18 cm", "18 apples") that the unit-stripping
  // tiers below would otherwise reduce to the bare number and accept.
  if (isPlainNumberVal(c) && !isPlainNumberVal(s)) return false;

  // Both sides are plain numbers — compare by value so a thousands separator ("1,200" vs "1200") matches.
  // The comma-decimal guard in plainNumericValue keeps "8,7" from being read as 87.
  if (isPlainNumberVal(c) && isPlainNumberVal(s)) {
    const sn = plainNumericValue(s);
    const cn = plainNumericValue(c);
    if (sn !== null && cn !== null && Math.abs(sn - cn) < 1e-9) return true;
  }

  // Mark scheme to N d.p. ("4.7") — a more precise working value that rounds to it ("4.71") is accepted.
  if (roundedDecimalPrecisionMatch(submitted, correct)) return true;

  // Strip prime/apostrophe notation so A′ matches A (coordinate geometry labelling).
  const sP = stripPrime(s);
  const cP = stripPrime(c);
  if (sP && cP && sP === cP) return true;

  const sF = normalizeVal(normalizeLatexFraction(s));
  const cF = normalizeVal(normalizeLatexFraction(c));
  if (sF === cF) return true;
  if (timesMatch(sF, cF)) return true;
  const sU = stripCommonUnit(sF);
  const cU = stripCommonUnit(cF);
  if (sU !== "" && sU === cU) return true;
  if (isUnitEquivalent(submitted, correct)) return true;
  const sO = normalizeOrdinal(sF);
  const cO = normalizeOrdinal(cF);
  if (sF !== "" && sO === cO) return true;

  const correctAlts = extractCorrectAlternatives(cF);
  const altMatcher = (sp, ca) =>
    sp === ca ||
    stripPrime(sp) === stripPrime(ca) ||
    timesMatch(sp, ca) ||
    (stripCommonUnit(sp) !== "" && stripCommonUnit(sp) === stripCommonUnit(ca)) ||
    normalizeOrdinal(sp) === normalizeOrdinal(ca) ||
    isUnitEquivalent(sp, ca);

  if (correctAlts.some((ca) => altMatcher(sF, ca))) return true;
  if (correctAlts.length > 1) return false;

  const submittedCandidates = extractAnswerCandidates(submitted)
    .map((v) => normalizeVal(stripMarkSchemeOperatorPrefix(v)))
    .filter(Boolean);
  for (const sc of submittedCandidates) {
    if (sc === cF) return true;
    if (timesMatch(sc, cF)) return true;
    if (stripCommonUnit(sc) !== "" && stripCommonUnit(sc) === stripCommonUnit(cF)) return true;
    if (normalizeOrdinal(sc) === normalizeOrdinal(cF)) return true;
    if (isUnitEquivalent(sc, cF)) return true;
  }

  // A lone number mistyped with a comma decimal separator ("3,00", "£3,5") must NOT be salvaged by
  // splitting on the comma and matching its first fragment ("3") — it's one malformed number, not a
  // value list. Skip the fragment fallback for it so "3,00" fails against a "£3.00"/"3" mark scheme.
  if (!isSingleMalformedNumber(submitted)) {
    const submittedParts = splitMultiValueAnswer(sF);
    if (submittedParts.some((sp) => altMatcher(sp, cF))) return true;
  }

  const sN = normalizeHyphensAndBrackets(submitted);
  const cN = normalizeHyphensAndBrackets(correct);
  if (sN && cN && sN === cN) return true;
  if (extractCorrectAlternatives(cN).some((ca) => sN === ca)) return true;

  // Last resort: a word-boundary typo ("Super market" vs "Supermarket") — compare with all
  // whitespace removed. Tried only after every other tier fails, so genuinely distinct
  // multi-word answers (which would already have matched above if equivalent) are unaffected.
  // Word answers only: collapsing spaces between digits changes numeric meaning, so a bare fraction
  // "17/11" must not match the mixed number "1 7/11" (a different value, 17/11 ≠ 18/11) by fusing
  // away the whole-number/fraction separator space. Mirrors the equivalent guard in
  // FuzzyAnswerMatching::isFuzzyTextMatchSingle (PHP) so the two stay in agreement.
  const sNoSpace = s.replace(/\s+/g, "");
  const cNoSpace = c.replace(/\s+/g, "");
  if (sNoSpace && cNoSpace && sNoSpace === cNoSpace
    && /\p{L}/u.test(sNoSpace) && /\p{L}/u.test(cNoSpace)
    && !/\d\s+\d/.test(s) && !/\d\s+\d/.test(c)) return true;

  return false;
};

export const isAnswerPartial = (submitted, correct, questionText = "") => {
  if (submitted == null || submitted === "" || submitted === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  if (countQuestionNumericAnswerEquivalent(submitted, correct, questionText)) return false;
  const correctStr = String(correct);
  if (extractCorrectAlternatives(correctStr).length > 1) return false;
  const correctParts = splitMultiValueAnswer(correctStr);
  if (correctParts.length <= 1) return false;
  const submittedParts = splitMultiValueAnswer(String(submitted));
  if (submittedParts.length >= correctParts.length) return false;
  return submittedParts.some((sp) => correctParts.some((cp) => singleValueMatches(sp, cp)));
};

// A review-row answer value may be a multi-fill array or a TypeDraw { text, drawing } object
// rather than a plain string — reduce it to the comparable text form.
export const answerValueText = (value) => {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    if ("text" in value || "drawing" in value) return value.text ?? "";
  }
  return value;
};

// Array-aware wrappers used by review pages, where multi-fill answers arrive as arrays that
// must match positionally (blank i ↔ correct[i]) — a value in the wrong box scores nothing.
export const isAnswerCorrectFlexible = (submitted, correct, options = {}) => {
  const submittedVal = answerValueText(submitted);
  if (submittedVal == null || submittedVal === "" || submittedVal === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  // Each blank is compared with isAnswerCorrect, not singleValueMatches: ONE blank can hold a list of
  // values ("66°, 66°" for "the two other angles"), and only isAnswerCorrect matches those as a set.
  // singleValueMatches compares each of the student's values against the whole mark scheme, so it
  // failed even the authored answer — and the blanks stay positional either way (blank i ↔ correct[i]),
  // so a value in the wrong box still scores nothing.
  if (Array.isArray(submittedVal) && Array.isArray(correct)) {
    if (correct.length === 0) return false;
    return correct.every((c, i) => isAnswerCorrect(submittedVal[i] ?? "", c, options));
  }
  if (Array.isArray(submittedVal)) return isAnswerCorrect(submittedVal[0] ?? "", correct, options);
  if (Array.isArray(correct)) return isAnswerCorrect(submittedVal, correct[0] ?? "", options);
  return isAnswerCorrect(submittedVal, correct, options);
};

export const isAnswerPartialFlexible = (submitted, correct) => {
  const submittedVal = answerValueText(submitted);
  if (Array.isArray(submittedVal) || Array.isArray(correct)) {
    const sub = Array.isArray(submittedVal) ? submittedVal : [submittedVal];
    const cor = Array.isArray(correct) ? correct : [correct];
    if (cor.length <= 1) return false;
    let matched = 0;
    cor.forEach((c, i) => {
      if (singleValueMatches(sub[i] ?? "", c)) matched++;
    });
    return matched > 0 && matched < cor.length;
  }
  return isAnswerPartial(submittedVal, correct);
};

// The question demands the fraction be reduced ("Write your answer in its simplest form", "give your
// answer in its lowest terms"). Simplifying IS the task, so an unreduced equivalent — 6/8 for 3/4 — is
// a wrong answer despite having the same numeric value, and the value-equivalence tiers must not
// accept it. Mirrors FuzzyAnswerMatching::questionAsksForSimplestForm on the backend.
export const questionAsksForSimplestForm = (question) => {
  if (!question) return false;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  return /\bsimplest\s+form\b/.test(text)
    || /\blowest\s+terms\b/.test(text)
    || /\bsimplif(?:y|ied|ying)\b/.test(text)
    || /\bfully\s+reduced\b/.test(text);
};

// The question demands the answer be WRITTEN as a fraction ("What fraction of …?", "give your answer as
// a fraction"). Expressing it IS the task, so the equal-valued decimal (0.25 for 1/4) is the wrong form —
// but the client's value-equality fraction tier accepts it, and so does the backend's AI grader. Distinct
// from questionAsksForSimplestForm, which polices how far a fraction is reduced.
// Mirrors FuzzyAnswerMatching::questionAsksForFractionForm.
export const questionAsksForFractionForm = (question) => {
  if (!question) return false;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  return /\b(?:what|which)\s+fraction\b/.test(text)
    || /\bas\s+a\s+fraction\b/.test(text)
    || /\bin\s+fraction\s+form\b/.test(text);
};

const plainAnswerText = (v) =>
  decodeHtmlEntities(decodeHtmlSpaces(String(v ?? "")).replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ").trim();

// A value that IS a fraction — "3/4", "1 1/2", "\frac{3}{4}" — as opposed to its decimal value.
// Mirrors FuzzyAnswerMatching::valueIsFraction.
export const valueIsFraction = (v) => {
  const t = plainAnswerText(v).toLowerCase();
  return /^-?\d+\s*\/\s*\d+$/.test(t) || /^-?\d+\s+\d+\s*\/\s*\d+$/.test(t) || t.includes("\\frac");
};

// A backslash that isn't opening a LaTeX command or delimiter. There is no arithmetic a student can
// mean by it: "(30 × 10) \ 50" is not "(30 × 10) ÷ 50".
export const hasStrayBackslash = (v) => /\\(?![a-zA-Z()[\]{}\\])/.test(plainAnswerText(v));

// clockwise vs anticlockwise/counter-clockwise — the same word set, the opposite answer. Every
// word-containment tier reads "clockwise" as a substring of "counter-clockwise" and says yes.
const rotationDirection = (v) => {
  const t = plainAnswerText(v).toLowerCase().replace(/[^a-z]/g, "");
  if (!t) return null;
  // Checked first: "anticlockwise" and "counterclockwise" both CONTAIN "clockwise".
  if (t.includes("anticlockwise") || t.includes("counterclockwise") || t.includes("ccw")) return "anticlockwise";
  if (t.includes("clockwise") || t === "cw") return "clockwise";
  return null;
};

export const rotationDirectionConflict = (submitted, correct) => {
  const s = rotationDirection(submitted);
  const c = rotationDirection(correct);
  return s !== null && c !== null && s !== c;
};

// The same families are also synonyms of each other ("anticlockwise" = "counter-clockwise" = "ccw"),
// which no tier below knows. Only when the whole value IS the direction word: "turns clockwise 5 times"
// carries more than the direction, so it stays for the normal tiers to judge.
const pureRotationDirection = (v) => {
  const t = plainAnswerText(v).toLowerCase().replace(/[^a-z]/g, "");
  return ["anticlockwise", "counterclockwise", "ccw", "clockwise", "cw"].includes(t) ? rotationDirection(t) : null;
};

export const rotationDirectionsAgree = (submitted, correct) => {
  const s = pureRotationDirection(submitted);
  return s !== null && s === pureRotationDirection(correct);
};

// A range mark scheme ("between 4 and 8 …") is only answered by BOTH endpoints — naming one ("4", or
// the unfinished "4 and") is a different, wrong claim about the range.
export const submittedMissesRangeEndpoints = (submitted, correct) => {
  const m = plainAnswerText(correct).toLowerCase()
    .match(/\b(?:between|from)\s+(-?\d+(?:\.\d+)?)\s*(?:and|to|–|-)\s*(-?\d+(?:\.\d+)?)/);
  if (!m) return false;
  const s = plainAnswerText(submitted);
  return [m[1], m[2]].some((endpoint) => {
    const esc = endpoint.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return !new RegExp(`(?<![\\d.])${esc}(?![\\d.])`).test(s);
  });
};

// A submission cut off mid-phrase ("4 and", "between 4 and") — the student never finished it.
export const submittedIsDanglingFragment = (submitted) => {
  const s = plainAnswerText(submitted).toLowerCase().replace(/[\s.,;:]+$/, "");
  return s !== "" && /\b(?:and|or|to|of|with|than|between|from|plus|minus|because)$/.test(s);
};

// A mark scheme offering alternative TUPLES of values — "12,8 or 12,4 or 4,8" for "write down a pair of
// possible numbers". Each alternative is a fixed-size list, so the answer is exactly one of them: how many
// values ("a pair" is two) is part of the answer, and which values go together is too. Null for every
// other shape of mark scheme, which other tiers own.
// Mirrors FuzzyAnswerMatching::correctIsValueTupleChoice.
export const correctIsValueTupleChoice = (correct) => {
  const alternatives = extractCorrectAlternatives(plainAnswerText(correct));
  if (alternatives.length < 2) return null;
  const tuples = [];
  for (const alt of alternatives) {
    const values = String(alt).split(",").map((v) => v.trim()).filter(Boolean);
    if (values.length < 2 || values.some((v) => !/^-?\d+(?:\.\d+)?$/.test(v))) return null;
    tuples.push(values.map((v) => String(parseFloat(v))));
  }
  // The alternatives must agree on size — "a pair" is two, whichever pair you pick.
  if (new Set(tuples.map((t) => t.length)).size !== 1) return null;
  return tuples;
};

// True when the submission is not exactly one of the offered tuples, order within it being free.
// Mirrors FuzzyAnswerMatching::submittedMissesValueTupleChoice.
export const submittedMissesValueTupleChoice = (submitted, correct) => {
  const tuples = correctIsValueTupleChoice(correct);
  if (tuples === null) return false;
  const given = plainAnswerText(submitted).split(/[,\s]+/).map((v) => v.trim()).filter(Boolean);
  // Only value lists are judged here; anything else is left to the other tiers.
  if (!given.length || given.some((v) => !/^-?\d+(?:\.\d+)?$/.test(v))) return false;
  const canon = (a) => a.map((v) => String(parseFloat(v))).sort().join("|");
  const g = canon(given);
  return !tuples.some((t) => canon(t) === g);
};

// Wrong on notation/completeness grounds alone, whatever it means. The lenient tiers (prose
// containment, the backend's AI graders) read straight past exactly these, so the client's
// upgrade-the-backend fallbacks check it before accepting an answer.
// Mirrors FuzzyAnswerMatching::submissionIsDeterministicallyWrong.
export const submissionIsDeterministicallyWrong = (submitted, correct) => {
  if (String(submitted ?? "").trim() === "") return false;
  const namedPair = correctIsNamedPair(correct);
  if (namedPair && !namedPairMatches(submitted, namedPair)) return true;
  // "=/" is a ≠, not an "=" with a stray slash — but that is exactly how the AI reads it.
  const expectedOperator = canonicalComparisonOperator(correct);
  if (expectedOperator && canonicalComparisonOperator(submitted) !== expectedOperator) return true;
  // A power mark scheme with a dropped/wrong power (e.g. "1+2³+…" for "1³+2³+…") must not be
  // upgraded by the prose / value-equivalence client fallbacks.
  if (isNumericPowerExpression(correct) && !matchesNumericPower(submitted, correct)) return true;
  // "Write down A PAIR of numbers" answered with three ("12,4,8") — every prose/AI tier finds the required
  // pair among the values and reads straight past the extra one.
  if (submittedMissesValueTupleChoice(submitted, correct)) return true;
  return hasStrayBackslash(submitted)
    || rotationDirectionConflict(submitted, correct)
    || submittedMissesRangeEndpoints(submitted, correct)
    || submittedIsDanglingFragment(submitted)
    // An incomplete "list all the ways" answer is a run of lines lifted verbatim from the mark scheme,
    // so isShortValueInProseAnswer finds every one of them and upgrades the backend's correct "partial".
    || (correctIsSumEnumeration(correct) && !sumEnumerationMatches(submitted, correct));
};

const gcdOf = (a, b) => (b === 0 ? a : gcdOf(b, a % b));

// A fraction (or the fractional part of a mixed number) already in lowest terms. A value that isn't a
// fraction at all (whole number, decimal, prose) isn't "unsimplified" — returns true so other tiers own
// it. Mirrors FuzzyAnswerMatching::fractionIsInSimplestForm on the backend.
export const fractionIsInSimplestForm = (value) => {
  const v = normalizeLatexFraction(decodeHtmlSpaces(String(value ?? "")).replace(/<[^>]*>/g, ""))
    .trim()
    .toLowerCase();
  let n;
  let d;
  let m = v.match(/^-?\d+\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    n = parseInt(m[1], 10);
    d = parseInt(m[2], 10);
  } else if ((m = v.match(/^(-?\d+)\s*\/\s*(\d+)$/))) {
    n = Math.abs(parseInt(m[1], 10));
    d = parseInt(m[2], 10);
  } else {
    return true;
  }
  if (!d || !n) return true;
  return gcdOf(n, d) === 1;
};

// A question whose answer is an ordered sequence — order matters, so a correct set of values in
// the wrong order must not be accepted (e.g. "list these words in order, starting with the lowest").
export const requiresOrderedAnswer = (question) => {
  if (!question) return false;
  const type = String(question.type || question.question_type || "").toLowerCase();
  if (type === "ordering") return true;
  const text = decodeHtmlSpaces(String(question.question_text || question.text || question.question || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!text) return false;
  return /\bin\s+(?:ascending|descending|increasing|decreasing|numerical|alphabetical|reverse)?\s*order\b/.test(text)
    || /\border\s+of\b/.test(text)
    || /\b(?:arrange|rearrange|list|order|put|rank|sort|reorder)\b[^.]*\border\b/.test(text)
    || /\bstarting\s+with\s+the\s+(?:lowest|highest|smallest|largest|biggest|first)\b/.test(text)
    || /\bfrom\s+(?:lowest|highest|smallest|largest|biggest)\s+to\b/.test(text);
};

// A mark scheme that is a SEQUENCE rather than an unordered set: every part is numeric and some value
// repeats (e.g. the digital-root pattern "1, 4, 9, 7, 7, 9, 4, 1"). Order carries meaning, so the same
// multiset in a different order is a different answer. A set of DISTINCT values (factors "1, 2, 7, 14")
// stays order-insensitive. Mirrors FuzzyAnswerMatching::correctIsNumericSequence on the backend.
export const correctIsNumericSequence = (correct) => {
  const parts = splitMultiValueAnswer(String(correct ?? ""));
  if (parts.length < 3) return false;
  if (!parts.every((p) => /^-?\d+(?:\.\d+)?$/.test(String(p).trim()))) return false;
  const normalized = parts.map((p) => String(parseFloat(String(p).trim())));
  return new Set(normalized).size < normalized.length;
};

// True when the submitted values ARE the expected values, just sequenced differently — the only case
// that may be reported as "wrong order". A list with a wrong/missing value is plainly incorrect, and
// saying "wrong order" about it (as the greedy in-order scan used to) misleads the student.
export const isSameValueMultiset = (submittedParts, correctParts) => {
  if (submittedParts.length !== correctParts.length) return false;
  const used = new Set();
  for (const cp of correctParts) {
    let hit = false;
    for (let si = 0; si < submittedParts.length; si++) {
      if (used.has(si)) continue;
      if (singleValueMatches(submittedParts[si], cp)) {
        used.add(si);
        hit = true;
        break;
      }
    }
    if (!hit) return false;
  }
  return true;
};

export const isAnswerCorrect = (submitted, correct, options = {}) => {
  if (submitted == null || submitted === "" || submitted === "—") return false;
  if (correct == null || correct === "" || correct === "—") return false;
  // "List ALL the ways to make 60p" — every sum in the mark scheme is required, so decide the whole
  // list by set equality before the tiers below, which credit a subset of it as a full match.
  if (correctIsSumEnumeration(correct)) return sumEnumerationMatches(submitted, correct);
  // A route / order of labelled points ("A E D B C G") — the whole sequence is the answer.
  if (correctIsLabelSequence(correct)) return labelSequenceMatches(submitted, correct);
  // "Which TWO cities…" — both names are the answer, in either order.
  const namedPair = correctIsNamedPair(correct);
  if (namedPair) return namedPairMatches(submitted, namedPair);
  if (options.ordered || correctIsNumericSequence(correct)) {
    const correctOrdered = splitMultiValueAnswer(String(correct));
    if (correctOrdered.length > 1) {
      let submittedOrdered = splitMultiValueAnswer(String(submitted));
      // When the correct sequence is all numbers, students often separate them with spaces
      // ("798 879 897") or bare commas ("798,879,897"), which splitMultiValueAnswer doesn't break.
      // Re-tokenize on any whitespace/comma run so a correct space-separated list isn't failed.
      const correctAllNumeric = correctOrdered.every((p) => /^-?\d+(?:\.\d+)?$/.test(String(p).trim()));
      if (correctAllNumeric && submittedOrdered.length < correctOrdered.length) {
        const reSplit = String(submitted).trim().split(/[\s,]+/).map((p) => p.trim()).filter(Boolean);
        if (reSplit.length > submittedOrdered.length) submittedOrdered = reSplit;
      }
      if (submittedOrdered.length !== correctOrdered.length) return false;
      return correctOrdered.every((cp, i) => singleValueMatches(submittedOrdered[i], cp));
    }
  }
  if (singleValueMatches(submitted, correct)) return true;
  if (countQuestionNumericAnswerEquivalent(submitted, correct, options.questionText)) return true;
  const correctParts = splitMultiValueAnswer(String(correct));
  const submittedParts = splitMultiValueAnswer(String(submitted));
  if (correctParts.length > 1 && submittedParts.length > 1) {
    let matched = 0;
    const usedSubmitted = new Set();
    for (const cPart of correctParts) {
      for (let si = 0; si < submittedParts.length; si++) {
        if (usedSubmitted.has(si)) continue;
        if (singleValueMatches(submittedParts[si], cPart)) {
          matched++;
          usedSubmitted.add(si);
          break;
        }
      }
    }
    if (matched === correctParts.length) return true;
    // Submitting a subset of required values is partial, not correct — fall through.
  }
  return false;
};
